import { deleteNotification, getMyNotifications, markNotificationsRead, setNotificationRead } from "api/notifications";
import type { NotificationDto } from "api/types";
import TopNav from "pages/TreeViewPage/TopNav";
import ViewIcon from "pages/TreeViewPage/ViewIcon";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Paths, { getTreeViewPath } from "routes/Paths";
import { useSession } from "Session";
import { formatLastModified, notificationCopy } from "utilities";

import styles from "./NotificationsPage.module.css";

type ApiError = {
	status?: number;
};

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

/** Full-page list of the user's notifications with an explicit mark-all-read action. */
const NotificationsPage = () => {
	const { t } = useTranslation("homePage");
	const navigate = useNavigate();
	const { getAccessToken, signOut } = useSession();
	const [notifications, setNotifications] = useState<NotificationDto[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isMarking, setIsMarking] = useState(false);

	useEffect(() => {
		let isCurrent = true;

		const load = async (): Promise<void> => {
			const token = await getAccessToken();
			if (!token || !isCurrent) {
				return;
			}

			const loaded = await getMyNotifications(token);
			if (!isCurrent) {
				return;
			}

			setNotifications(loaded);
		};

		load()
			.catch((error: unknown) => {
				if ((error as ApiError).status === 401) {
					signOut();
				}
			})
			.finally(() => {
				if (isCurrent) {
					setIsLoading(false);
				}
			});

		return () => {
			isCurrent = false;
		};
	}, [getAccessToken, signOut]);

	const unreadCount = useMemo(
		() => notifications.filter((notification: NotificationDto) => !notification.isRead).length,
		[notifications]
	);

	const onMarkAllRead = useCallback(async (): Promise<void> => {
		if (isMarking) {
			return;
		}

		const token = await getAccessToken();
		if (!token) {
			return;
		}

		setIsMarking(true);
		try {
			await markNotificationsRead(token);
			setNotifications((current: NotificationDto[]) =>
				current.map((notification: NotificationDto) => ({ ...notification, isRead: true }))
			);
		} catch {
			// Leave the unread state untouched so the user can retry.
		} finally {
			setIsMarking(false);
		}
	}, [getAccessToken, isMarking]);

	const onNotificationClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>): void => {
			const treeId = event.currentTarget.dataset.treeId;
			if (!treeId) {
				return;
			}

			// A shared tree is not viewable until accepted, so shares land on "Shared with Me".
			if (event.currentTarget.dataset.notificationType === "TreeShared") {
				navigate(Paths.sharedWithMe);

				return;
			}

			navigate(getTreeViewPath(treeId));
		},
		[navigate]
	);

	const onToggleRead = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
			const notification = notifications.find(
				(item: NotificationDto) => item.id === event.currentTarget.dataset.notificationId
			);
			if (!notification) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			const next = !notification.isRead;
			setNotifications((current: NotificationDto[]) =>
				current.map((item: NotificationDto) => (item.id === notification.id ? { ...item, isRead: next } : item))
			);

			try {
				await setNotificationRead(notification.id, next, token);
			} catch {
				// Revert the optimistic update on failure.
				setNotifications((current: NotificationDto[]) =>
					current.map((item: NotificationDto) =>
						item.id === notification.id ? { ...item, isRead: !next } : item
					)
				);
			}
		},
		[getAccessToken, notifications]
	);

	const onDelete = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
			const notification = notifications.find(
				(item: NotificationDto) => item.id === event.currentTarget.dataset.notificationId
			);
			if (!notification) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			const previous = notifications;
			setNotifications((current: NotificationDto[]) =>
				current.filter((item: NotificationDto) => item.id !== notification.id)
			);

			try {
				await deleteNotification(notification.id, token);
			} catch {
				// Restore the list on failure so the notification is not silently lost.
				setNotifications(previous);
			}
		},
		[getAccessToken, notifications]
	);

	const messageFor = (notification: NotificationDto): string => {
		const copy = notificationCopy(notification);

		return t(copy.key, copy.values);
	};

	return (
		<div className={styles.page}>
			<TopNav />

			<main className={styles.content}>
				<header className={styles.header}>
					<h1 className={styles.heading}>{t("view.notifications")}</h1>
					<button
						className={styles.markAllButton}
						disabled={isMarking || unreadCount === 0}
						onClick={onMarkAllRead}
						type="button">
						{t("view.markAllRead")}
					</button>
				</header>

				{!isLoading && notifications.length === 0 && (
					<p className={styles.emptyState}>{t("view.noNotifications")}</p>
				)}

				{notifications.length > 0 && (
					<div className={styles.list}>
						{notifications.map((notification: NotificationDto) => (
							<div
								key={notification.id}
								className={notification.isRead ? styles.item : styles.unreadItem}>
								<button
									className={styles.itemMain}
									data-notification-type={notification.type}
									data-tree-id={notification.treeId}
									onClick={onNotificationClick}
									type="button">
									<span className={styles.actorAvatar}>
										{notification.actorImageUrl ? (
											<img alt="" referrerPolicy="no-referrer" src={notification.actorImageUrl} />
										) : (
											getInitials(notification.actorName)
										)}
									</span>
									<span className={styles.itemBody}>
										<span className={styles.itemMessage}>{messageFor(notification)}</span>
										<span className={styles.itemTime}>
											{formatLastModified(notification.createdDate)}
										</span>
									</span>
								</button>
								{!notification.isRead && <span aria-hidden="true" className={styles.unreadDot} />}
								<div className={styles.itemActions}>
									<button
										aria-label={notification.isRead ? t("view.markAsUnread") : t("view.markAsRead")}
										className={styles.itemActionButton}
										data-notification-id={notification.id}
										onClick={onToggleRead}
										title={notification.isRead ? t("view.markAsUnread") : t("view.markAsRead")}
										type="button">
										<ViewIcon name={notification.isRead ? "mail" : "mailOpen"} />
									</button>
									<button
										aria-label={t("view.deleteNotification")}
										className={styles.itemDeleteButton}
										data-notification-id={notification.id}
										onClick={onDelete}
										title={t("view.deleteNotification")}
										type="button">
										<ViewIcon name="trash" />
									</button>
								</div>
							</div>
						))}
					</div>
				)}
			</main>
		</div>
	);
};

export default NotificationsPage;
